import React, { useState, useContext } from "react";
import {
  Button,
  TextField,
  Autocomplete,
  Chip,
  Stack,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axios from "axios";
import UserContext from "../../../UserContext";
import SlidingSidebar, {
  ButtonControls,
} from "../../../Components/SlidingSidebar";

interface TagOption {
  id: number;
  name: string;
}

interface AddStaffProps {
  onSuccess?: () => void;
}

const AddStaff: React.FC<AddStaffProps> = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "Account",
    tags: [] as string[],
    user_access: {
      tags: [] as string[],
      user_status: {
        Admin: "No",
        Active: "Yes",
        Verified: "Yes",
      },
    },
  });
  const [availableTags, setAvailableTags] = useState<TagOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { selectedAccountId, currentUser } = useContext(UserContext) || {};

  const getStateCode = () => {
    if (!currentUser?.accounts_json || !selectedAccountId) return "NA";

    const selectedAccount = currentUser.accounts_json.find(
      (account: { account_id: number }) =>
        account.account_id === selectedAccountId
    );

    return selectedAccount?.state || "NA";
  };

  const handleOpen = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/settings/util-data?tables=tags`
      );
      setAvailableTags(response.data.tags || []);
      setOpen(true);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      role: "Account",
      tags: [],
      user_access: {
        tags: [],
        user_status: {
          Admin: "No",
          Active: "Yes",
          Verified: "Yes",
        },
      },
    });
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      const stateCode = getStateCode();
      const requestData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role,
        account_id: selectedAccountId,
        user_access: {
          tags: formData.tags,
          state_code: stateCode,
          user_status: formData.user_access.user_status,
        },
      };

      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/users/create`,
        requestData
      );

      if (response.status === 201) {
        handleClose();
        setSnackbarOpen(true);
        if (typeof onSuccess === "function") {
          onSuccess();
        }
      }
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to create user");
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpen}
        startIcon={<CloudUploadIcon />}
      >
        Add User
      </Button>

      <SlidingSidebar open={open} onClose={handleClose} width="500px">
        <Stack spacing={0.5} sx={{ p: 2, height: "100%" }}>
          <Paper elevation={0} sx={{ p: 1 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Basic Information</Typography>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                size="small"
                fullWidth
                required
              />
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                size="small"
                fullWidth
                required
              />
              <TextField
                label="Email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                size="small"
                fullWidth
                required
              />
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ p: 1 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Role & Tags</Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  label="Role"
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                >
                  <MenuItem value="Account">Account</MenuItem>
                  <MenuItem value="Owner">Owner</MenuItem>
                </Select>
              </FormControl>

              <Autocomplete
                multiple
                options={availableTags}
                getOptionLabel={(option: TagOption) => option.name}
                onChange={(_, newValue) => {
                  setFormData({
                    ...formData,
                    tags: newValue.map((v: TagOption) => v.name),
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Tags" size="small" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({ index })}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))
                }
              />
            </Stack>
          </Paper>

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Stack>

        <ButtonControls>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Create User
          </Button>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
        </ButtonControls>
      </SlidingSidebar>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          User created successfully
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddStaff;
